// Unit 03 — Ethernet and MAC Addresses (Verification Activity)
import { predict, observe, readConfirm } from '../../ui/widgets.js';
import { star } from './topologies.js';
import { ran, pinged, sameMac, normIf, yes, no, macCisco } from './helpers.js';

const layout = {
  width: 900, height: 330,
  nodes: {
    PC1: { x: 110, y: 90, type: 'pc', sub: '192.168.10.10' },
    PC2: { x: 110, y: 250, type: 'pc', sub: '192.168.10.11' },
    PC3: { x: 760, y: 170, type: 'pc', sub: '192.168.10.12' },
    SW1: { x: 430, y: 170, type: 'switch', sub: 'Catalyst 2960 · just rebooted' },
  },
  zones: [{ x: 20, y: 25, w: 860, h: 290, label: 'OFFICE LAN · VLAN 1 · 192.168.10.0/24' }],
};
const learnedPort = (rt, mac) => rt.net.devices.SW1.macTable.find((e) => e.mac === mac)?.port;

export default {
  id: 'u03', num: '03', title: 'Ethernet and MAC Addresses', type: 'Verification Activity', implemented: true,
  objective: 'Read MAC addresses on endpoints and switches, and prove how a switch learns which port each MAC lives on.',
  network: () => star({ pcs: ['PC1', 'PC2', 'PC3'], ports: [1, 2, 7] }),
  layout, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Ethernet frames and MAC addresses', workspace: false,
      content: `
<p>Ethernet moves <b>frames</b> between devices on the same link or switched network. Every frame carries a destination and a source <b>MAC address</b>.</p>
<pre>| Preamble | Dest MAC (6B) | Src MAC (6B) | Type 0x0800 (IPv4) | Payload 46–1500 B | FCS (4B) |</pre>
<ul>
<li>A MAC address is 48 bits, written in hex. Windows shows <code>00-50-79-66-68-00</code>; Cisco shows <code>0050.7966.6800</code>. Same address.</li>
<li>The first 24 bits are the <b>OUI</b> (manufacturer). The last 24 bits are assigned by that manufacturer.</li>
<li><code>ffff.ffff.ffff</code> is the broadcast MAC — every device on the segment accepts it.</li>
<li>The <b>FCS</b> is a checksum. Frames that fail it are counted as <i>CRC errors</i> and dropped.</li>
</ul>`,
      widgets: [readConfirm({ id: 'u03-read1' })],
      question: 'Why does a MAC address alone not tell you <i>where</i> a device is on the network?',
    },
    {
      id: 'understand', phase: 'UNDERSTAND', title: 'How a switch learns', workspace: false,
      content: `
<p>A switch builds its <b>MAC address table</b> (CAM table) by reading the <b>source</b> MAC of every frame it receives:</p>
<ol>
<li><b>Learn</b> — frame arrives on Fa0/1 with source MAC A → record “A is on Fa0/1”.</li>
<li><b>Forward</b> — if the destination MAC is in the table, send the frame out of that one port only.</li>
<li><b>Flood</b> — if the destination is unknown (or broadcast), send it out of every other port in the VLAN.</li>
<li><b>Age</b> — entries that are not refreshed expire (300 s by default).</li>
</ol>
<div class="callout">A switch never learns from destination addresses. A device that has not transmitted anything is invisible to the switch.</div>`,
      widgets: [readConfirm({ id: 'u03-read2' })],
      question: 'PC2 is powered on but idle. Will SW1 know which port PC2 is on?',
    },
    {
      id: 'predict', phase: 'PREDICT', title: 'Predict the MAC table', workspace: false,
      content: '<p>SW1 was rebooted five minutes ago and none of the PCs have sent traffic since. You will then ping PC3 from PC1.</p>',
      widgets: [predict({
        id: 'u03-pred', prompt: 'After PC1 pings PC3, which MAC addresses will SW1 have learned dynamically?',
        options: ['None — ping does not use MAC addresses', 'PC1 and PC3 only', 'PC1, PC2 and PC3', 'Only PC3'],
      })],
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'Prove how SW1 learns', devices: ['PC1', 'PC2', 'PC3', 'SW1'],
      content: `
<p>Work in this order — the order matters:</p>
<ol>
<li>On <b>SW1</b>: <code>enable</code> then <code>show mac address-table dynamic</code> — capture the table <i>before</i> any traffic.</li>
<li>On <b>PC1</b>: find its MAC with <code>getmac</code> or <code>ipconfig /all</code>.</li>
<li>On <b>PC1</b>: <code>ping 192.168.10.12</code> (PC3).</li>
<li>On <b>SW1</b>: show the MAC address table again and compare.</li>
</ol>`,
      evidence: [
        { id: 'empty', text: 'SW1: MAC table captured while it had no dynamic entries', match: (ev) => ran('SW1', ['show mac address-table dynamic', 'show mac address-table'])(ev) && !/DYNAMIC/.test(ev.output) },
        { id: 'mac', text: 'PC1: its own MAC address displayed', match: ran('PC1', ['getmac', 'ipconfig /all']) },
        { id: 'ping', text: 'PC1: successful ping to PC3', match: pinged('PC1', '192.168.10.12', { success: true }) },
        { id: 'after', text: 'SW1: MAC table captured after the ping shows learned entries', match: (ev) => ran('SW1', ['show mac address-table dynamic', 'show mac address-table'])(ev) && /DYNAMIC/.test(ev.output) },
      ],
      widgets: [observe({
        id: 'u03-obs', title: 'MAC TABLE FINDINGS', intro: 'Answer from SW1’s own output — the values are checked against the switch.',
        fields: [
          { label: 'PC1 MAC address, exactly as SW1 displays it', placeholder: 'xxxx.xxxx.xxxx', check: (v, rt) => (/^[0-9a-f]{4}\.[0-9a-f]{4}\.[0-9a-f]{4}$/i.test(v) ? (sameMac(v, rt.net.devices.PC1.nic.mac) || 'That is not PC1’s MAC.') : 'Use Cisco dotted format, e.g. 0050.7966.6800') },
          { label: 'SW1 port where PC3’s MAC was learned', placeholder: 'Fa0/x', check: (v, rt) => { const p = learnedPort(rt, rt.net.devices.PC3.nic.mac); if (!p) return 'SW1 has not learned PC3 yet — generate traffic and look again.'; return normIf(v) === normIf(p) || 'Check the Ports column for PC3’s MAC.'; } },
          { label: 'Is PC2’s MAC in SW1’s table right now? (yes / no)', check: (v, rt) => { const has = !!learnedPort(rt, rt.net.devices.PC2.nic.mac); return (has ? yes(v) : no(v)) || 'Read the table again — does PC2’s MAC appear?'; } },
        ],
      })],
      question: 'The table shows PC3 on the port in the Ports column — but the diagram label might be wrong. Which do you trust, and why?',
      hints: ['On a switch you need privileged mode first: <code>enable</code>, then <code>show mac address-table</code>.', 'PC2 never sent a frame in this test, so SW1 had nothing to learn from it.'],
    },
    {
      id: 'continue', phase: 'CONTINUE', title: 'What you proved',
      workspace: false,
      content: (rt) => {
        const p = rt.widget('u03-pred').data.choice;
        return `<p>You predicted: <b>${p || '—'}</b>.</p>
<p>The evidence showed SW1 learned <b>PC1</b> (<code>${macCisco(rt.net.devices.PC1.nic.mac)}</code>) from the ARP broadcast and echo request, and <b>PC3</b> from its replies. PC2 stayed invisible because it never transmitted.</p>
<p>This is why <code>show mac address-table</code> is one of the first commands you run when a user “can’t connect”: it proves whether the switch has <i>ever heard from</i> the device, and on which port.</p>`;
      },
      question: 'A user swears their PC is plugged into Fa0/12, but the MAC table shows their MAC on Fa0/19. What is your next step?',
    },
  ],
};
